// shared-lib/cabinet-runs/cornerJoin.js
//
// Join derived per-wall runs that meet at a room corner into ONE polyline run
// (#1494, layout-relationships §5.3). The engine already fills a multi-segment
// run and plans a corner cabinet at each interior vertex (`corners.js`); this
// is only the DERIVATION half — deciding that two runs are one counter.
//
// A join needs both: the two near ends reach the walls' shared vertex within
// `reach` (a scanned cabinet stops a cabinet-depth short of the corner; the
// corner unit fills exactly that), and, with both ends extended to the vertex,
// `isCorner` holds (different walls, a real turn — collinear captured segments
// of one wall keep joining through the scan frame clusters, never here).
//
// Pure. Runs are RESOLVED (`segments[i].wall` present). The merged run keeps
// the first run's id; the other run's per-wall id becomes an alias
// (`runIds.js`). Bays / overrides / appliances of the appended run have their
// `segmentIndex` re-based; corner overrides keep `cornerIndex` (interior
// vertices are numbered from 0 along the merged run).

const {
  getWallAxes,
  wallParamToSurfacePoint,
} = require('../kitchen-assembly/wallMath');
const { isCorner, CORNER_CABINET_FOOTPRINT } = require('./corners');
const { intersectLinesXZ } = require('../geometry/wallGeometry');
const { runAliases } = require('./runIds');
const { baysForRun } = require('./bays');

const CORNER_JOIN_REACH = CORNER_CABINET_FOOTPRINT; // 36"
const VERTEX_EPS = 0.02; // 2 cm — two captured walls "share" a corner

function wallVertices(wall) {
  const ax = getWallAxes(wall);
  if (!ax) return [];
  const a = wallParamToSurfacePoint(wall, 0);
  const b = wallParamToSurfacePoint(wall, ax.wallLen);
  return [a, b].filter(Boolean);
}

/** The vertex two walls share, or null. */
function sharedWallVertex(wallA, wallB) {
  if (!wallA || !wallB || wallA.id === wallB.id) return null;
  for (const p of wallVertices(wallA)) {
    for (const q of wallVertices(wallB)) {
      if (Math.hypot(p.x - q.x, p.z - q.z) <= VERTEX_EPS) {
        return { x: (p.x + q.x) / 2, z: (p.z + q.z) / 2 };
      }
    }
  }
  return null;
}

/**
 * The vertex where one wall's END stands on the other wall's line — a
 * peninsula arm on the wall it springs from, or the arm it springs from; a
 * real wall whose end lands on a longer wall's face (an L-shaped room whose
 * long wall runs past the corner) — or null.
 *
 * Two real walls usually share a room vertex. An arm does not: its virtual wall line
 * ENDS on the other wall's line (a peninsula's root stands on its anchor wall,
 * one back-panel inch inboard of the wall's end when flush, further in when
 * the run stops at a doorway; a floating-U's far arm roots on the back arm's
 * tip the same way). The run turns at that root. So the vertex is the arm's
 * end that lies on the other wall's line, within that wall's span — measured
 * perpendicular to the wall, since the root is on the line by construction
 * and the wall's own vertex may be an inch or a doorway away. Two arms whose
 * ends were snapped to one point are caught by `sharedWallVertex` first.
 */
function armRootVertex(wallA, wallB) {
  const pairs = [
    [wallA, wallB],
    [wallB, wallA],
  ];
  for (const [arm, wall] of pairs) {
    if (!arm || !wall) continue;
    const ax = getWallAxes(wall);
    if (!ax) continue;
    for (const p of wallVertices(arm)) {
      const dx = p.x - ax.start.x;
      const dz = p.z - ax.start.z;
      const perp = Math.abs(dx * ax.wallNormal.x + dz * ax.wallNormal.z);
      const param = dx * ax.wallDir.x + dz * ax.wallDir.z;
      if (perp > VERTEX_EPS) continue;
      if (param < -VERTEX_EPS || param > ax.wallLen + VERTEX_EPS) continue;
      return { x: p.x, z: p.z };
    }
  }
  return null;
}

/**
 * The vertex the two segments are EXTENDED to: where the walls' axes cross,
 * when that is within reach of the candidate the vertex finders gave.
 *
 * A shared room vertex is the MIDPOINT of two captured wall ends up to
 * `VERTEX_EPS` apart, and an arm root is one wall's end, which may sit a few
 * millimetres past the other's start (a wall `getKitchenFinalWalls` clipped
 * to its cabinet stretch starts 8 mm short of the corner on 66616D52). Either
 * way the point lies on at most one axis, and `isCorner` asks the two
 * extended ends to coincide within a millimetre — so the join said "not a
 * corner" to a pair whose walls plainly cross. The crossing is the corner;
 * both projections land on it exactly. Parallel axes keep the candidate
 * (`isCorner` rejects them as collinear anyway).
 */
function joinVertex(wallA, wallB, candidate) {
  const axA = getWallAxes(wallA);
  const axB = getWallAxes(wallB);
  if (!axA || !axB) return candidate;
  const x = intersectLinesXZ(axA.start, axA.end, axB.start, axB.end);
  if (!x) return candidate;
  if (Math.hypot(x.x - candidate.x, x.z - candidate.z) > 2 * VERTEX_EPS) {
    return candidate;
  }
  return { x: x.x, z: x.z };
}

function reverseRun(run) {
  const n = run.segments.length;
  const flip = (i) => n - 1 - i;
  return {
    ...run,
    segments: [...run.segments]
      .reverse()
      .map((s) => ({ ...s, startParam: s.endParam, endParam: s.startParam })),
    ...(run.bays
      ? {
          bays: run.bays.map((b) => ({
            ...b,
            segmentIndex: flip(b.segmentIndex ?? 0),
          })),
        }
      : {}),
    ...(run.appliances
      ? {
          appliances: run.appliances.map((x) => ({
            ...x,
            segmentIndex: flip(x.segmentIndex ?? 0),
          })),
        }
      : {}),
    ...(run.bayOverrides
      ? {
          bayOverrides: run.bayOverrides.map((o) =>
            o.segmentIndex != null
              ? { ...o, segmentIndex: flip(o.segmentIndex) }
              : o.cornerIndex != null
                ? { ...o, cornerIndex: n - 2 - o.cornerIndex }
                : o
          ),
        }
      : {}),
  };
}

function rebase(run, segOffset, cornerOffset) {
  const shift = (list, key, off) =>
    (list || []).map((x) =>
      x[key] != null ? { ...x, [key]: x[key] + off } : x
    );
  const out = { ...run };
  if (run.bays) out.bays = shift(run.bays, 'segmentIndex', segOffset);
  if (run.appliances)
    out.appliances = shift(run.appliances, 'segmentIndex', segOffset);
  if (run.bayOverrides)
    out.bayOverrides = shift(
      shift(run.bayOverrides, 'segmentIndex', segOffset),
      'cornerIndex',
      cornerOffset
    );
  return out;
}

/** Distance from a segment end (param) to a world point. */
function endDistance(seg, end, vertex) {
  const p = wallParamToSurfacePoint(
    seg.wall,
    end === 'start' ? seg.startParam : seg.endParam
  );
  return p ? Math.hypot(p.x - vertex.x, p.z - vertex.z) : Infinity;
}

function paramAt(wall, point) {
  const ax = getWallAxes(wall);
  if (!ax) return null;
  const o = wallParamToSurfacePoint(wall, 0);
  return (point.x - o.x) * ax.wallDir.x + (point.z - o.z) * ax.wallDir.z;
}

/**
 * Try to attach `b` after `a` (a's END meets b's START at a corner). Returns
 * the merged run or null. `b` is tried as given and reversed by the caller.
 */
/**
 * The joined run's under-cabinet light record. The join keeps run a's
 * top-level fields, which would silently drop b's light — and the record is
 * what re-stamps the uppers on every solve, so the whole b leg would go dark.
 * Lit wins (one run, one setting, and either leg asked for it); an explicit
 * `false` survives only when neither is lit; neither set stays unset (never an
 * `undefined` key — Firestore rejects the room write).
 */
function joinedUnderCabinetLight(a, b) {
  const la = a.underCabinetLight;
  const lb = b.underCabinetLight;
  if (la === true || lb === true) return { underCabinetLight: true };
  const either = la ?? lb;
  return typeof either === 'boolean' ? { underCabinetLight: either } : {};
}

/**
 * The joined run's cabinet trim records, on the light's rule: the join keeps
 * run a's top-level fields, which would drop b's — and the records are what
 * stamp the carriers on every solve. Crown, light rail, the valance over
 * the sink and the end panels ON win (one run, one setting; where both are
 * on, a's own stays);
 * counted pieces are merged, counts of one piece and colour added. Unset
 * stays unset (no `undefined` key).
 */
function joinedCabinetTrim(a, b) {
  const out = {};
  for (const key of ['cabinetCrown', 'lightRail', 'valance', 'endPanels']) {
    const on = a[key] || b[key];
    if (on) out[key] = on;
    else if (a[key] === false || b[key] === false) out[key] = false;
  }
  if (a.extraMolding || b.extraMolding) {
    const merged = new Map();
    for (const row of [...(a.extraMolding || []), ...(b.extraMolding || [])]) {
      const k = `${row.piece}|${row.colour ?? ''}`;
      const prev = merged.get(k);
      merged.set(
        k,
        prev ? { ...prev, count: prev.count + row.count } : { ...row }
      );
    }
    out.extraMolding = [...merged.values()];
  }
  return out;
}

function tryJoin(a, b, reach, refuse = null, joinStraight = null) {
  const segA = a.segments[a.segments.length - 1];
  const segB = b.segments[0];
  if (!segA?.wall || !segB?.wall) return null;
  const candidate =
    sharedWallVertex(segA.wall, segB.wall) ||
    armRootVertex(segA.wall, segB.wall);
  if (!candidate) return null;
  const vertex = joinVertex(segA.wall, segB.wall, candidate);
  if (endDistance(segA, 'end', vertex) > reach) return null;
  if (endDistance(segB, 'start', vertex) > reach) return null;
  const pA = paramAt(segA.wall, vertex);
  const pB = paramAt(segB.wall, vertex);
  if (pA == null || pB == null) return null;
  const extA = { ...segA, endParam: pA };
  const extB = { ...segB, startParam: pB };
  // A run normally joins only at a corner. A caller may also join two legs
  // that carry straight on through the vertex (`joinStraight`) — a
  // generated run continuing past its wall's end in line with it; the fill
  // treats that vertex as no corner (`isCorner`), so the counter runs on.
  const corner = isCorner(extA, extB);
  if (!corner && !(joinStraight && joinStraight(extA, extB))) return null;
  // An oblique junction joins like any other: the engine plans it (the legs
  // stop on the bisector, a kite fills the wedge — `corners.js`), so a run
  // may turn through 135° as readily as 90° (user ruling, 2026-09-13; it
  // used to keep per-wall runs and the generator's corner-fill pieces).
  // The caller's own reason two legs are not one counter — an appliance
  // standing at the vertex ends the run there (`applianceSeparatesLegs`).
  // That speaks for a corner; a straight joint has none to claim.
  if (corner && refuse && refuse(extA, extB)) return null;
  // A hard piece beside the corner (a scanned range) does not block the
  // join: `fillRun`'s corner plan reads it as a blocker and grades the corner
  // blind / open, exactly as it does for an opening.
  const cornerCount = a.segments.length - 1;
  const rb = rebase(b, a.segments.length, cornerCount + 1);
  // Merge reservations through the MIGRATING reader, one canonical shape
  // out. A per-key concat produced a run carrying BOTH `bays` (one side) and
  // `appliances` (the other) — and `baysForRun` ignores `appliances` whenever
  // `bays` exists, so the legacy side's sink/range reservations silently
  // vanished on persist (round-2 audit M4). Only emit `bays` when some input
  // carried reservations at all; the joined run never carries the legacy key.
  const concat = (key) =>
    a[key] || b[key] ? { [key]: [...(a[key] || []), ...(rb[key] || [])] } : {};
  const anyReservations = a.bays || a.appliances || rb.bays || rb.appliances;
  // Strip the legacy key by DESTRUCTURE, never `appliances: undefined` — a
  // literal undefined survives the spread into the persisted run and
  // Firestore rejects the whole room write (found on the Kitchen Generator
  // apply, 2026-08-31).
  const { appliances: _legacyAppliances, ...aSansLegacy } = a;
  return {
    ...(anyReservations ? aSansLegacy : a),
    segments: [...a.segments.slice(0, -1), extA, extB, ...rb.segments.slice(1)],
    ...(anyReservations ? { bays: [...baysForRun(a), ...baysForRun(rb)] } : {}),
    ...concat('bayOverrides'),
    ...joinedUnderCabinetLight(a, b),
    ...joinedCabinetTrim(a, b),
    aliases: [...new Set([...runAliases(a), b.id, ...runAliases(b)])].filter(
      (id) => id !== a.id
    ),
    joinedRunIds: [
      ...(a.joinedRunIds || [a.id]),
      ...(b.joinedRunIds || [b.id]),
    ],
  };
}

/**
 * @param {Object[]} runs resolved runs
 * @param {{ reach?: number, refuse?: (segA, segB) => boolean, joinStraight?: (segA, segB) => boolean }} [opts]
 *   `refuse` vetoes a geometric join the caller knows is not one counter —
 *   the two segments are passed extended to the vertex. `joinStraight`
 *   allows a join where the legs carry straight on (no corner); off by
 *   default, so drawn and scanned runs never join that way.
 * @returns {{ runs: Object[], joins: {runIds: string[]}[] }} identity `runs`
 *   when nothing joins
 */
function joinRunsAtCorners(runs, opts = {}) {
  const reach = opts.reach ?? CORNER_JOIN_REACH;
  const refuse = opts.refuse || null;
  const joinStraight = opts.joinStraight || null;
  let pool = [...(runs || [])];
  const joins = [];
  let progressed = true;
  while (progressed) {
    progressed = false;
    outer: for (let i = 0; i < pool.length; i += 1) {
      for (let j = 0; j < pool.length; j += 1) {
        if (i === j) continue;
        const a = pool[i];
        const b = pool[j];
        const merged =
          tryJoin(a, b, reach, refuse, joinStraight) ||
          tryJoin(a, reverseRun(b), reach, refuse, joinStraight) ||
          tryJoin(reverseRun(a), b, reach, refuse, joinStraight) ||
          tryJoin(reverseRun(a), reverseRun(b), reach, refuse, joinStraight);
        if (!merged) continue;
        joins.push({ runIds: [a.id, b.id] });
        pool = pool.filter((_, k) => k !== i && k !== j);
        pool.push(merged);
        progressed = true;
        break outer;
      }
    }
  }
  if (!joins.length) return { runs, joins };
  return {
    runs: pool.map((r) => {
      const { joinedRunIds, ...rest } = r;
      return joinedRunIds ? { ...rest, joinedRunIds } : rest;
    }),
    joins,
  };
}

module.exports = {
  joinRunsAtCorners,
  joinedCabinetTrim,
  sharedWallVertex,
  armRootVertex,
  reverseRun,
  CORNER_JOIN_REACH,
  VERTEX_EPS,
};
